import { ILogger } from '@principle-theorem/developer-tools';
import {
  AppointmentStatus,
  IPatientMetadata,
  type IBrand,
  type IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  asyncForAll,
  bufferedQuery$,
  DocumentReference,
  Firestore,
  FirestoreMigrate,
  isSameRef,
  multiConcatMap,
  resolveSequentially,
  undeletedQuery,
  WithRef,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { Brand } from '../models/brand';
import { Patient } from '../models/patient/patient';
import { Appointment } from '../models/appointment/appointment';
import { cloneDeep, compact, isEmpty, set, uniqWith } from 'lodash';

export function applySchedulingReasonCounts(
  currentMetadata: IPatientMetadata,
  reasonCounts: { metadataKey: string; count: number }[]
): IPatientMetadata {
  const metadata = cloneDeep(currentMetadata);
  const totalsByKey = new Map<string, number>();
  for (const { metadataKey, count } of reasonCounts) {
    totalsByKey.set(metadataKey, (totalsByKey.get(metadataKey) ?? 0) + count);
  }
  for (const [metadataKey, total] of totalsByKey) {
    set(metadata, metadataKey, total);
  }
  return metadata;
}

export class SeedPatientSchedulingMetadata {
  constructor(private _logger: ILogger) {}

  async run(
    brandRef: DocumentReference<IBrand>,
    dryRun: boolean,
    patientRef?: DocumentReference<IPatient>
  ): Promise<void> {
    if (patientRef) {
      await this.updatePatient(patientRef, dryRun);
      return;
    }

    await this._getPatients$(brandRef)
      .pipe(
        multiConcatMap((patient) => this.updatePatient(patient.ref, dryRun))
      )
      .toPromise();
  }

  async updatePatient(
    patientRef: DocumentReference<IPatient>,
    dryRun: boolean
  ): Promise<void> {
    const patient = await Firestore.getDoc(patientRef);
    const appointments = await Firestore.getDocs(
      undeletedQuery(Patient.appointmentCol({ ref: patientRef }))
    );

    const cancelledAppointments = appointments.filter(
      (appointment) => appointment.status === AppointmentStatus.Cancelled
    );
    if (!cancelledAppointments.length) {
      return;
    }

    const reasonRefs = await resolveSequentially(
      cancelledAppointments,
      async (appointment) => {
        const events = await Firestore.getDocs(
          Appointment.schedulingEventCol(appointment)
        );
        return events.map((event) => event.reason);
      }
    );

    const flatReasonRefs = compact(reasonRefs.flat());
    const uniqueReasons = uniqWith(flatReasonRefs, isSameRef);
    const resolvedReasons = await asyncForAll(uniqueReasons, async (ref) =>
      Firestore.getDoc(ref)
    );
    const reasonCounts = resolvedReasons.map((reason) => ({
      metadataKey: reason.metadataKey,
      count: flatReasonRefs.filter((ref) => isSameRef(ref, reason.ref)).length,
    }));
    const metadata = applySchedulingReasonCounts(patient.metadata ?? {}, reasonCounts);

    if (isEmpty(metadata)) {
      return;
    }

    if (!dryRun) {
      this._logger.info(
        `Updating scheduling metadata for patient ${patientRef.path}`
      );
      await FirestoreMigrate.patchDoc(patient.ref, { metadata });
    }
  }

  private _getPatients$(
    brandRef: DocumentReference<IBrand>
  ): Observable<WithRef<IPatient>[]> {
    return bufferedQuery$(Brand.patientCol({ ref: brandRef }), 200, 'ref');
  }
}
